/** Thin typed client for the AERO API. */

import type { ApiErrorBody, ChatResponse } from "../types";

const API_BASE = (import.meta.env.VITE_API_BASE ?? "").replace(/\/+$/, "");

export class ApiError extends Error {
  readonly code: string;
  readonly status: number;

  constructor(message: string, code: string, status: number) {
    super(message);
    this.name = "ApiError";
    this.code = code;
    this.status = status;
  }
}

function isApiErrorBody(value: unknown): value is ApiErrorBody {
  return (
    typeof value === "object" &&
    value !== null &&
    "error" in value &&
    typeof (value as ApiErrorBody).error?.message === "string"
  );
}

export interface SendChatArgs {
  message: string;
  sessionId?: string | null;
  /** Budget in INR from the chat's budget slider, when the user set one. */
  budgetInr?: number | null;
  signal?: AbortSignal;
}

export async function sendChat({
  message,
  sessionId,
  budgetInr,
  signal,
}: SendChatArgs): Promise<ChatResponse> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message,
        session_id: sessionId ?? undefined,
        budget_inr: budgetInr ?? undefined,
      }),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(
      "Couldn't reach the advisor. Check your connection and try again.",
      "network_error",
      0,
    );
  }

  const data: unknown = await res.json().catch(() => null);

  if (!res.ok) {
    if (isApiErrorBody(data)) {
      throw new ApiError(data.error.message, data.error.code, res.status);
    }
    throw new ApiError("Something went wrong. Please try again.", "http_error", res.status);
  }

  return data as ChatResponse;
}

export interface StreamChatArgs extends SendChatArgs {
  /** Called with each text fragment as the reply streams in. */
  onDelta: (text: string) => void;
  /** Called with a short "what AERO is doing" label (e.g. while web-searching). */
  onStatus?: (text: string) => void;
}

/**
 * POST /api/chat/stream — reads the Server-Sent Events body, invokes `onDelta`
 * for every text fragment, and resolves with the final authoritative reply.
 * Falls back to throwing `ApiError` on transport / server errors.
 */
export async function streamChat({
  message,
  sessionId,
  budgetInr,
  signal,
  onDelta,
  onStatus,
}: StreamChatArgs): Promise<ChatResponse> {
  let res: Response;
  try {
    res = await fetch(`${API_BASE}/api/chat/stream`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        message,
        session_id: sessionId ?? undefined,
        budget_inr: budgetInr ?? undefined,
      }),
      signal,
    });
  } catch (err) {
    if (err instanceof DOMException && err.name === "AbortError") throw err;
    throw new ApiError(
      "Couldn't reach the advisor. Check your connection and try again.",
      "network_error",
      0,
    );
  }

  if (!res.ok || !res.body) {
    const data: unknown = await res.json().catch(() => null);
    if (isApiErrorBody(data)) {
      throw new ApiError(data.error.message, data.error.code, res.status);
    }
    throw new ApiError("Something went wrong. Please try again.", "http_error", res.status);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  let final: ChatResponse | null = null;

  const handleEvent = (raw: string) => {
    const line = raw.split("\n").find((l) => l.startsWith("data:"));
    if (!line) return;
    let evt: {
      type?: string;
      text?: string;
      reply?: string;
      session_id?: string;
      contract?: ChatResponse["contract"];
      budget_prompt?: ChatResponse["budget_prompt"];
      quick_replies?: string[] | null;
      message?: string;
    };
    try {
      evt = JSON.parse(line.slice(5).trim());
    } catch {
      return;
    }
    if (evt.type === "delta" && evt.text) {
      onDelta(evt.text);
    } else if (evt.type === "status" && evt.text) {
      onStatus?.(evt.text);
    } else if (evt.type === "done") {
      final = {
        session_id: evt.session_id ?? sessionId ?? "",
        reply: evt.reply ?? "",
        contract: evt.contract ?? null,
        budget_prompt: evt.budget_prompt ?? null,
        quick_replies: evt.quick_replies ?? null,
      };
    } else if (evt.type === "error") {
      throw new ApiError(evt.message ?? "Streaming failed.", "stream_error", 500);
    }
  };

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    let sep: number;
    while ((sep = buffer.indexOf("\n\n")) !== -1) {
      const chunk = buffer.slice(0, sep);
      buffer = buffer.slice(sep + 2);
      handleEvent(chunk);
    }
  }
  if (buffer.trim()) handleEvent(buffer);

  if (!final) {
    throw new ApiError("The reply ended unexpectedly. Please try again.", "stream_incomplete", 0);
  }
  return final;
}

export interface HealthInfo {
  status: string;
  version: string;
  environment: string;
  memory_backend: string;
  search_provider: string;
  model: string;
}

export async function fetchHealth(signal?: AbortSignal): Promise<HealthInfo> {
  const res = await fetch(`${API_BASE}/health`, { signal });
  if (!res.ok) {
    throw new ApiError("Health check failed.", "health_error", res.status);
  }
  return (await res.json()) as HealthInfo;
}
